import {Box, Button, Title} from '@mantine/core';
import type React from 'react';
import {useState} from 'react';
import {COLOR_AI_BACKGROUND} from '../../../../config/constants/colorCodes';
import {isReleasedAiToDoCreationFlow} from '../../../../config/unReleasePage';
import {ReportSummaryQuestionTypeID} from '../../../../core/entities/reportAnalytics/constants';
import type {ReportType} from '../../../../core/entities/reportAnalytics/schema';
import type {
  parseReportSummary,
  summaryContent,
  summaryContentAdvice,
  summaryContentGeneral,
  summaryContentQuestion,
  summaryContentStep,
  summaryContentThemaTexts,
} from '../../../../core/repositories/reportSummaryRepository';
import {RegistBox} from '../../../../shared/components/RegistBox';
import {AiTodoGenerateWithEntryMgtCreationContainer} from '../../../entryManagements/components/Container/Progress/AiTodoGenerateWithEntryMgtCreationContainer';
import {getAIGradient} from '../../usecase/getAIGradient';
import {
  AdviceBox,
  GeneralBox,
  QuestionBox,
  StepBox,
  ThemaTextsBox,
} from './SummaryContentBox';

type Props = {
  isSignedIn?: boolean;
  isStudentVerificationRequired?: boolean;
  isPC: boolean;
  summaries?: parseReportSummary[];
  reportType?: ReportType;
  companyID?: number;
  companyName?: string;
  departmentID?: number;
};

type ContentProps = {
  title: string;
  typeID: ReportSummaryQuestionTypeID;
  content: summaryContent | undefined;
  isPC: boolean;
  isSignedIn?: boolean;
  isStudentVerificationRequired?: boolean;
  index: number;
  reportType?: ReportType;
  companyID?: number;
  companyName?: string;
  departmentID?: number;
};

const ResultContentBox: React.FC<ContentProps> = ({
  title,
  typeID,
  content,
  isPC,
  index,
  reportType,
  isSignedIn = true,
  isStudentVerificationRequired = false,
  companyID,
  companyName,
  departmentID,
}) => {
  const [isAiTodoModalOpen, setIsAiTodoModalOpen] = useState(false);
  const isSelection = reportType === 'selection';

  const isQuestionContent = (
    c: summaryContent | undefined,
  ): c is summaryContentQuestion => {
    return typeID === ReportSummaryQuestionTypeID.QUESTION;
  };

  const questionTitle =
    typeID === ReportSummaryQuestionTypeID.QUESTION &&
    isSelection &&
    isQuestionContent(content)
      ? content?.data[0]?.step
      : '';

  const handleToggleAiTodoModal = () => {
    setIsAiTodoModalOpen((prev) => !prev);
  };

  const renderSignedInContent = <T,>(
    Component: React.FC<{
      content: T;
      isPC: boolean;
      isSelection?: boolean;
      isSignedIn: boolean;
    }>,
    c: T,
  ) => {
    if (isSignedIn) {
      if (c && Array.isArray((c as any).data)) {
        return (
          <Component
            content={c}
            isPC={isPC}
            isSelection={isSelection}
            isSignedIn={isSignedIn}
          />
        );
      }

      return null;
    }

    return (
      <>
        <Component
          content={c}
          isPC={isPC}
          isSelection={isSelection}
          isSignedIn={isSignedIn}
        />
      </>
    );
  };

  const renderContent = () => {
    switch (typeID) {
      case ReportSummaryQuestionTypeID.STEP: {
        return content ? (
          <StepBox content={content as summaryContentStep} />
        ) : null;
      }

      case ReportSummaryQuestionTypeID.QUESTION: {
        return renderSignedInContent(
          QuestionBox,
          content as summaryContentQuestion,
        );
      }

      case ReportSummaryQuestionTypeID.ADVICE: {
        return renderSignedInContent(
          AdviceBox,
          content as summaryContentAdvice,
        );
      }

      case ReportSummaryQuestionTypeID.ES_QUESTION:
      case ReportSummaryQuestionTypeID.ES_POINT: {
        return renderSignedInContent(
          GeneralBox,
          content as summaryContentGeneral,
        );
      }

      case ReportSummaryQuestionTypeID.WEB_TEST:
      case ReportSummaryQuestionTypeID.GD:
      case ReportSummaryQuestionTypeID.OTHER: {
        return renderSignedInContent(
          ThemaTextsBox,
          content as summaryContentThemaTexts,
        );
      }

      case ReportSummaryQuestionTypeID.NULL: {
        return null;
      }
    }
  };

  return (
    <>
      <Box id={`summary-type-${index}`} className="w-full mb-20 p-4">
        <Box className="w-full flex items-center gap-12 mb-8 flex-wrap">
          <Title
            order={2}
            style={{
              background: getAIGradient(),
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {questionTitle !== '' && questionTitle
              ? `${questionTitle}での質問内容`
              : title}
          </Title>
          {/* STEPタイトル横にAI ToDo生成ボタン */}
          {typeID === ReportSummaryQuestionTypeID.STEP &&
            isReleasedAiToDoCreationFlow() &&
            isSignedIn && (
              <Button
                onClick={handleToggleAiTodoModal}
                size="xs"
                style={{
                  background: getAIGradient(),
                  border: 'none',
                }}
              >
                この選考のToDoをAIで生成する
              </Button>
            )}
        </Box>
        <Box
          className="w-full text-black-1 rounded-12 p-12 relative"
          style={{backgroundColor: COLOR_AI_BACKGROUND}}
        >
          <Box className="p-8">
            <Box>{renderContent()}</Box>
          </Box>
          {!isSignedIn && typeID !== ReportSummaryQuestionTypeID.STEP && (
            <RegistBox
              isStudentVerificationRequired={isStudentVerificationRequired}
            />
          )}
        </Box>
      </Box>

      {/* AI ToDo生成モーダル (STEPの場合のみ) */}
      {typeID === ReportSummaryQuestionTypeID.STEP &&
        isSignedIn &&
        companyID &&
        companyName &&
        departmentID &&
        reportType && (
          <AiTodoGenerateWithEntryMgtCreationContainer
            opened={isAiTodoModalOpen}
            onClose={() => setIsAiTodoModalOpen(false)}
            companyID={companyID}
            companyName={companyName}
            departmentID={departmentID}
            reportType={reportType}
          />
        )}
    </>
  );
};

export const ResultSummaryBox: React.FC<Props> = ({
  isSignedIn,
  isStudentVerificationRequired,
  summaries,
  isPC,
  reportType,
  companyID,
  companyName,
  departmentID,
}) => {
  return (
    <Box className="w-full flex items-center justify-center pc:p-24 p-12 bg-white">
      <Box className="w-full">
        {summaries?.map((summary, index) => (
          <ResultContentBox
            key={`summary-result-content-box-${index}`}
            typeID={summary.typeID}
            title={summary.typeName}
            content={summary.summary}
            isPC={isPC}
            isSignedIn={isSignedIn}
            isStudentVerificationRequired={isStudentVerificationRequired}
            index={index}
            reportType={reportType}
            companyID={companyID}
            companyName={companyName}
            departmentID={departmentID}
          />
        ))}
      </Box>
    </Box>
  );
};
